import { Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import ConfirmDialog from "@buildpy/ui/confirm-dialog";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { issueProjectConnectCode } from "@/api/actions/project-connect";
import { useRecordAction } from "@/modules/project/ui/use-record-action";

interface Props {
  projectId: string;
  code: string | null;
  closed: boolean;
  className?: string;
}

// 고객이 앱에서 넣는 주소 — 웹 /connect 와 같다.
export const CONNECT_PATH = "/connect";

// 읽기 쉽게 네 자리씩 끊는다(AB12-CD34) · 고객 입력은 하이픈을 무시한다.
export const connectCodeLabel = (code: string): string => code.replace(/(.{4})(?=.)/g, "$1-");

// 다시 만들기 확인 문구 — 고객이 쥔 옛 코드가 바로 죽으니 한 번 더 묻는다.
export const REISSUE_CONFIRM = {
  title: "현장 코드를 다시 만들까요?",
  description: "옛 코드는 바로 못 써요. 고객에게 새 코드를 다시 알려 주세요.",
  confirmLabel: "다시 만들기",
} as const;

const copy = async (code: string) => {
  try {
    await navigator.clipboard.writeText(code);
    toast.success("현장 코드를 복사했습니다");
  } catch {
    toast.error("복사하지 못했습니다");
  }
};

// 현장 연결 코드 — 고객이 앱·웹 /connect 에서 코드 + 전화 끝 4자리로 이 현장을 연다 · 재발급하면 옛 코드는 바로 죽는다.
const ConnectCodeCard = ({ projectId, code, closed, className }: Props) => {
  const { pending, run } = useRecordAction();
  const [ask, setAsk] = useState(false);
  const issue = () =>
    void run("현장 코드를 만들지 못했습니다", () => issueProjectConnectCode(projectId), () =>
      toast.success(code ? "새 현장 코드를 만들었습니다 · 옛 코드는 더 쓸 수 없습니다" : "현장 코드를 만들었습니다")
    );

  return (
    <SectionCard className={className} data-testid="project-connect-card" padding="sm">
      <h2 className="mb-2 text-sm font-semibold">현장 코드</h2>
      {code ? (
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-lg font-semibold tracking-wider" data-testid="project-connect-code">
            {connectCodeLabel(code)}
          </span>
          <Button aria-label="현장 코드 복사" size="icon" variant="ghost" onClick={() => void copy(code)}>
            <Copy />
          </Button>
        </div>
      ) : (
        <p className="text-muted-foreground" data-testid="project-connect-empty">
          아직 만든 코드가 없습니다.
        </p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">
        고객이 {CONNECT_PATH} 에서 이 코드와 휴대폰 끝 4자리로 공사 화면을 엽니다.
      </p>
      {!closed && (
        <Button className="mt-2" data-testid="project-connect-issue" disabled={pending} size="compact" variant="outline" onClick={code ? () => setAsk(true) : issue}>
          {code ? "다시 만들기" : "코드 만들기"}
        </Button>
      )}
      <ConfirmDialog
        confirmLabel={REISSUE_CONFIRM.confirmLabel}
        description={REISSUE_CONFIRM.description}
        open={ask}
        testId="project-connect-reissue-confirm"
        title={REISSUE_CONFIRM.title}
        onConfirm={issue}
        onOpenChange={setAsk}
      />
    </SectionCard>
  );
};

export default ConnectCodeCard;
